import { useQuery } from "@apollo/client";
import { GET_ALL_ACTIVITIES } from "../../../../../ActivityLibrary/queries";
import {
  AllActivitiesResponse,
  DataRow,
  ActivitiesFilteringTableProps,
} from "./types";
import { CREATE_PLAYLIST_COLUMNS } from "./columns";
import {
  secondsToMMSS,
  transformDate,
} from "../../../../../../utils/date-utils";
import { useState, useMemo, useCallback, useEffect } from "react";
import { SearchBar } from "components/molecules/SearchBar";
import {
  Alert,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Snackbar,
  Typography,
  Stack,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { StatusView, VoiceMessage } from "types/api";
import { colors } from "../../../../../../styles/variables";
import { StyledDataTable } from "components/molecules/StyledDataTable";
import { VoiceMessageList } from "../../../../../../components/molecules/VoiceMessageList";
import { sortVoiceMessages } from "utils/voice-message-utils";
import { getEnumName } from "types/utils";
import { PlaylistContainer } from "components/molecules/Modal/styles";
import { differenceInDays, parseISO } from "date-fns";
import { useSpecialistDropdown } from "hooks/useSpecialistDropDown";
import { useDifficultyEnums } from "hooks/useDifficultyEnums";

export const ActivitiesFilteringTable = (
  props: ActivitiesFilteringTableProps
) => {
  const [filterText, setFilterText] = useState<string>("");
  const [resetPaginationToggle, setResetPaginationToggle] =
    useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<DataRow[]>([]);
  const [toggleCleared, setToggleCleared] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>([]);

  const { difficultyEnums } = useDifficultyEnums();

  const {
    options: specialistOptions,
    prefixMapping,
    loading: specialistsLoading,
  } = useSpecialistDropdown();

  const { data, loading, error } = useQuery<AllActivitiesResponse>(
    GET_ALL_ACTIVITIES,
    {
      fetchPolicy: "network-only",
      variables: {
        inLibrary: true,
        statusView: getEnumName(StatusView, StatusView.Active),
      },
    }
  );

  useEffect(() => {
    setIsLoading(loading || specialistsLoading);
  }, [loading, specialistsLoading]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  useEffect(() => {
    setResetPaginationToggle((prev) => !prev);
  }, [difficultyFilter]);

  useEffect(() => {
    props.getDatainSeconds ? props.getDatainSeconds([]) : null;
  }, []);

  useEffect(() => {
    props.setAreRowsSelected(selectedRows.length > 0);
  }, [selectedRows]);

  const currentPlaylistActivityIds = props?.playlistActivities?.map(
    (p) => p?.id
  );

  const getRecorderName = (recorderId?: number) => {
    if (!recorderId) return "";
    const specialist = specialistOptions.find(
      (option) => option.value === recorderId
    );
    return specialist ? specialist.label : "";
  };

  const getRecorderPrefix = (recorderId?: number) => {
    if (!recorderId || !prefixMapping) return "";
    return prefixMapping[recorderId] || "";
  };

  const createPlaylistData =
    data?.voiceMessages?.data?.filter((activity) => !activity?.deletedAt) ?? [];

  const tableData: DataRow[] = createPlaylistData
    .filter((activity) => {
      return !currentPlaylistActivityIds.includes(activity.id);
    })
    .filter((activity) => {
      return activity?.title !== null && activity?.feature !== null;
    })
    .map((activity) => {
      const recorderName = getRecorderName(activity?.recordedBy?.id);

      const allFields: string = [
        activity?.title,
        activity?.feature,
        recorderName,
        activity?.cognitiveDomains?.join(" "),
        transformDate(activity?.createdOn),
        activity?.difficulty,
      ]
        .map((x) => x ?? "")
        .join(" ")
        .toLowerCase();

      const idsOtherThanCurrentPlaylist =
        props.filteredPVMs
          ?.map((pvm) => pvm?.voiceMessage)
          ?.map((vm) => vm?.id ?? 0)
          ?.filter((x) => x !== 0) ?? [];

      const previouslyUsedId =
        idsOtherThanCurrentPlaylist?.includes(activity?.id) ?? false;
      const inCurrentPlaylist =
        currentPlaylistActivityIds?.includes(activity?.id) ?? false;

      const createdOnDate = parseISO(activity?.createdOn ?? "");
      const isNew =
        createdOnDate.toString() !== "Invalid Date" &&
        differenceInDays(new Date(), createdOnDate) <= 14;
      const newlyCreated = isNew ? "New" : "";

      return {
        id: activity?.id,
        recorder: recorderName,
        title: activity?.title ?? "",
        previouslyUsed: previouslyUsedId && !inCurrentPlaylist,
        templateLink: activity.templateLink,
        length: secondsToMMSS(activity?.audioFile?.durationInSeconds),
        cognitiveDomains: activity?.cognitiveDomains ?? [],
        recording: activity?.audioFile?.downloadUrl,
        allFields,
        voiceMessage: activity,
        newlyCreated,
        difficulty: activity?.difficulty,
        feature: activity?.feature,
      };
    });

  const filteredData: DataRow[] = tableData
    ?.filter((activity) =>
      activity?.allFields?.includes(filterText.toLowerCase())
    )
    ?.filter(
      (activity) =>
        !difficultyFilter ||
        difficultyFilter.length === 0 ||
        (activity.difficulty && difficultyFilter.includes(activity.difficulty))
    );

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);
    props.getDatainSeconds ? props.getDatainSeconds(state.selectedRows!) : null;
  }, []);

  const ConfirmAddToPlaylist = () => {
    return (
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          <Typography>
            Are you sure you want to add these to the playlist?
          </Typography>
          <VoiceMessageList
            isDragDisabled={true}
            voiceMessages={selectedRows.map((row) => row.voiceMessage)}
          />
        </DialogContent>
        <DialogActions>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="stretch"
            sx={{ gap: 1 }}
          >
            <Button
              className="button"
              onClick={() => {
                setConfirmOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              className="button"
              onClick={() => {
                props.getDatainSeconds ? props.getDatainSeconds([]) : null;
                setConfirmOpen(false);
                handleAdd();
              }}
            >
              Confirm
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  const LaskWeekPlaylistNumber = Number(props?.lastWeekTitle);

  const getSpecialistActivities = (recorderId: number, prefix: string) => {
    return createPlaylistData.filter((activity) => {
      return (
        activity?.recordedBy?.id === recorderId &&
        (activity?.title === "Intro" ||
          activity?.title === "Outro" ||
          activity?.title === "Transition-0" ||
          activity?.title === "Transition-1" ||
          activity?.title === "Transition-2" ||
          activity?.title ===
            (`${prefix}-Week-${LaskWeekPlaylistNumber + 1}-Feedback` ||
              `${prefix}-week-${LaskWeekPlaylistNumber + 1}-feedback`))
      );
    });
  };

  const specialRecorderMapping = useMemo(() => {
    const mapping: Record<string, { id: number; prefix: string }> = {};

    specialistOptions.forEach((option) => {
      const recorderId = option.value as number;
      const recorderName = option.label;
      const recorderPrefix = getRecorderPrefix(recorderId);

      if (recorderPrefix) {
        mapping[recorderName] = {
          id: recorderId,
          prefix: recorderPrefix,
        };
      }
    });

    return mapping;
  }, [specialistOptions, prefixMapping]);

  const currentRecorderActivities = useMemo(() => {
    const recorderInfo = Object.entries(specialRecorderMapping).find(
      ([name]) => name === props.th
    );

    if (recorderInfo) {
      const [_, info] = recorderInfo;
      return getSpecialistActivities(info.id, info.prefix);
    }
    return [];
  }, [props.th, createPlaylistData, LaskWeekPlaylistNumber]);

  useEffect(() => {
    if (!props.th || !specialRecorderMapping[props.th]) return;

    let defaultLength = 0;
    currentRecorderActivities.forEach((message) => {
      defaultLength += message?.audioFile?.durationInSeconds ?? 0;
    });

    props?.setDefaultLength(defaultLength);
  }, [currentRecorderActivities, props.th]);

  const defaultVoiceMessages = useMemo(() => {
    if (!props.th || !specialRecorderMapping[props.th]) {
      return sortVoiceMessages(props.playlistActivities);
    }

    return props.playlistActivities?.length > 0
      ? sortVoiceMessages(props.playlistActivities)
      : sortVoiceMessages(props.playlistActivities)?.concat(
          currentRecorderActivities
        );
  }, [props.playlistActivities, currentRecorderActivities, props.th]);

  const handleAdd = () => {
    setToggleCleared(!toggleCleared);
    const selectedTitlesAndIds = selectedRows?.map(
      (row: DataRow) => row.voiceMessage
    );

    if (props.th && specialRecorderMapping[props.th]) {
      if (props.playlistActivities?.length > 0) {
        props.setPlaylistActivities(
          sortVoiceMessages(props.playlistActivities)?.concat(
            selectedTitlesAndIds
          )
        );
      } else {
        props.setPlaylistActivities(
          sortVoiceMessages(props.playlistActivities)
            ?.concat(currentRecorderActivities)
            ?.concat(selectedTitlesAndIds)
        );
      }
    } else {
      props.setPlaylistActivities(
        sortVoiceMessages(props.playlistActivities)?.concat(
          selectedTitlesAndIds
        )
      );
    }

    setSelectedRows([]);
  };

  const checktableData = filteredData.filter((activity) => {
    const recorderId = activity?.voiceMessage?.recordedBy?.id;
    const recorderPrefix = getRecorderPrefix(recorderId);
    const currentRecorderInfo = props.th
      ? specialRecorderMapping[props.th]
      : null;

    if (recorderId && recorderPrefix) {
      const isCurrentRecorderView =
        currentRecorderInfo && currentRecorderInfo.id === recorderId;

      if (isCurrentRecorderView || props.th === getRecorderName(recorderId)) {
        return (
          activity?.title !== "Intro" &&
          activity?.title !== "Outro" &&
          activity?.title !== "Transition-0" &&
          activity?.title !== "Transition-1" &&
          activity?.title !== "Transition-2" &&
          activity?.title !==
            (`${recorderPrefix}-Week-${LaskWeekPlaylistNumber + 1}-Feedback` ||
              `${recorderPrefix}-week-${LaskWeekPlaylistNumber + 1}-feedback`)
        );
      }
    }

    return true;
  });

  const activityData = checktableData;

  const contextActions = useMemo(() => {
    return (
      <Button onClick={() => setConfirmOpen(true)}>Add to Playlist</Button>
    );
  }, [data, selectedRows, toggleCleared]);

  const AdditionalContextBar = () => {
    const pluralizer = selectedRows.length > 1 ? "s" : "";
    return (
      <Collapse in={selectedRows.length > 0}>
        <Paper sx={{ p: 1, bgcolor: `${colors.paleBlue}` }} elevation={0}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ gap: 2 }}
          >
            <Typography>
              {selectedRows.length} item{pluralizer} selected
            </Typography>
            <Button onClick={() => setConfirmOpen(true)}>
              Add to Playlist
            </Button>
          </Grid>
        </Paper>
      </Collapse>
    );
  };

  const handleClear = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText("");
    }
  };

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });

  const sortedMessages = defaultVoiceMessages
    .filter((message) => message?.title !== undefined)
    .sort((a, b) => {
      return collator.compare(a.title!, b.title!);
    });

  return (
    <>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <PlaylistContainer>
          <ConfirmAddToPlaylist />
          <StyledDataTable
            title="All Activities & Recordings*"
            columns={CREATE_PLAYLIST_COLUMNS}
            data={
              props.playlistActivities?.length > 0 ? filteredData : activityData
            }
            pagination
            paginationPerPage={10}
            selectableRows
            subHeader
            subHeaderComponent={
              <Stack
                direction="row"
                spacing={2}
                alignItems={"center"}
                justifyContent={"end"}
                width="100%"
                marginBottom={2}
              >
                <Autocomplete
                  multiple
                  options={difficultyEnums}
                  value={difficultyFilter}
                  onChange={(_, newValue) => setDifficultyFilter(newValue)}
                  openOnFocus
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Difficulty"
                      placeholder={
                        difficultyFilter && difficultyFilter.length > 0
                          ? ""
                          : "Select difficulties"
                      }
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      // TODO: Fix this the next time the file is edited.
                      // eslint-disable-next-line react/jsx-key
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                  }
                  sx={{
                    width:
                      difficultyFilter && difficultyFilter.length > 0
                        ? "fit-content"
                        : 150,
                    minWidth:
                      difficultyFilter && difficultyFilter.length > 0
                        ? "fit-content"
                        : 150,
                    maxWidth:
                      difficultyFilter && difficultyFilter.length > 0
                        ? "fit-content"
                        : 150,
                    "& .MuiInputLabel-root": {
                      display: "flex",
                      alignItems: "center",
                      transform:
                        difficultyFilter && difficultyFilter.length > 0
                          ? "translate(14px, -9px) scale(0.75)"
                          : "translate(14px, 12px) scale(1)",
                      "&.Mui-focused": {
                        transform: "translate(14px, -9px) scale(0.75)",
                      },
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      display: "flex",
                      flexWrap: "nowrap",
                      overflow: "hidden",
                      alignItems: "center",
                      paddingRight: "32px",
                      gap: "4px",
                      "& .MuiAutocomplete-input": {
                        minWidth:
                          difficultyFilter && difficultyFilter.length > 0
                            ? "0"
                            : "0",
                        width:
                          difficultyFilter && difficultyFilter.length > 0
                            ? "0 !important"
                            : "0",
                        padding: "0",
                        flexShrink: 0,
                        flexGrow: 0,
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        position: "absolute",
                        right: "9px",
                      },
                      "& .MuiChip-root": {
                        height: "24px",
                        maxWidth: "100px",
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      },
                    },
                  }}
                  limitTags={3}
                  disableCloseOnSelect
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                />
                <SearchBar
                  onChange={setFilterText}
                  onClear={handleClear}
                  searchText={filterText}
                />
              </Stack>
            }
            onSelectedRowsChange={handleRowSelected}
            contextActions={contextActions}
            clearSelectedRows={toggleCleared}
            paginationResetDefaultPage={resetPaginationToggle}
          />
          <AdditionalContextBar />
          <Typography
            style={{
              margin: "10px 0",
            }}
          >
            <strong>Playlist To Be Submitted: </strong>
          </Typography>
          <VoiceMessageList
            setPlaylistActivities={props.setPlaylistActivities}
            isDragDisabled={false}
            voiceMessages={
              props.playlistActivities?.length > 0
                ? defaultVoiceMessages
                : sortVoiceMessages(sortedMessages)
            }
            showIcon={
              props.playlistActivities?.length > 0 ? props.editMode : false
            }
            onListIconClick={(activity: VoiceMessage) => {
              props.setPlaylistActivities(
                props.playlistActivities?.filter(
                  (act) => act?.id != activity?.id
                )
              );
            }}
          />
        </PlaylistContainer>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch activity library data.
        </Alert>
      </Snackbar>
    </>
  );
};
